export interface IParsedAgentTaskDetails {
  customQuery?: string;
  workbookContext?: string;
  plainText?: string;
  errorMessage?: string;
}

/**
 * Parses the details field of an AgentTask.
 * Resolves potential JSON structure containing input parameters,
 * and extracts any appended error messages.
 */
export const parseAgentTaskDetails = (detailsStr?: string): IParsedAgentTaskDetails => {
  if (!detailsStr) return {};

  const result: IParsedAgentTaskDetails = {};

  // First, check if there is an appended error message at the end
  // (prefixed by \nERROR:, ERROR:, \nGemini FAILED: or Gemini FAILED:)
  let cleanStr = detailsStr.trim();
  
  const errorPatterns = [
    { marker: 'ERROR:', regex: /(?:^|\n)ERROR:\s*([\s\S]+)$/i },
    { marker: 'Gemini FAILED:', regex: /(?:^|\n)Gemini FAILED:\s*([\s\S]+)$/i }
  ];

  for (const pattern of errorPatterns) {
    const match = cleanStr.match(pattern.regex);
    if (match) {
      result.errorMessage = match[1].trim();
      // Remove the error part from the details string to extract the original inputs
      cleanStr = cleanStr.substring(0, cleanStr.lastIndexOf(match[0])).trim();
      break;
    }
  }

  // Now try to parse what is left as JSON
  try {
    const parsed = JSON.parse(cleanStr);
    if (parsed && typeof parsed === 'object') {
      if (parsed.customQuery) {
        result.customQuery = parsed.customQuery;
      }
      if (parsed.workbookContext) {
        result.workbookContext = parsed.workbookContext;
      }
      
      const { customQuery, workbookContext, ...rest } = parsed;
      if (Object.keys(rest).length > 0) {
        result.plainText = JSON.stringify(rest, null, 2);
      }
    } else {
      result.plainText = cleanStr;
    }
  } catch (e) {
    // If not valid JSON, treat the whole cleanStr as plainText details
    if (cleanStr) {
      result.plainText = cleanStr;
    }
  }

  return result;
};

/**
 * Generates a clean, short text summary of the task inputs for list views.
 */
export const getDetailsSummary = (details?: string): string => {
  if (!details) return '-';
  const parsed = parseAgentTaskDetails(details);
  if (parsed.customQuery) {
    return parsed.customQuery;
  }
  if (parsed.plainText) {
    return parsed.plainText;
  }
  if (parsed.workbookContext) {
    return `Lähdeaineisto (${parsed.workbookContext.length} merkkiä)`;
  }
  return '-';
};
